// Entity Health Score — gives every entity a visible "pulse".
// Backend (/api/entity-health) returns { entity_id, status, score, signals[] }.
import React from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";

export const HEALTH_META = {
  healthy: { color: "#34D399", labelKey: "health_healthy" },
  needs_attention: { color: "#FBBF24", labelKey: "health_needs_attention" },
  at_risk: { color: "#F87171", labelKey: "health_at_risk" },
};

const SEVERITY_COLOR = { high: "#F87171", medium: "#FBBF24", low: "#A1A1AA", info: "#60A5FA" };

// Where each signal should deep-link inside the entity (entity tab via ?tab=).
// Signals without an entry just open the entity page.
const SIGNAL_TAB = {
  overdue_invoices: "invoices",
  unpaid_rent: "rents",
  expiring_docs: "documents",
  overdue_tasks: "tasks",
  upcoming_obligations: "tasks",
  stalled_goals: "spaces",
  open_major_bets: "spaces",
};

export function signalPath(key, entityKey) {
  if (!entityKey) return null;
  const tab = SIGNAL_TAB[key];
  return tab ? `/entity/${entityKey}?tab=${tab}` : `/entity/${entityKey}`;
}

// One-line plain-text summary (used as a native tooltip in the sidebar).
export function healthSummary(health, t) {
  if (!health) return "";
  const meta = HEALTH_META[health.status];
  const label = t(meta?.labelKey || "health_healthy");
  const reasons = (health.signals || []).map(
    (s) => t(`health_sig_${s.key}`) + (s.count > 1 ? ` (${s.count})` : "")
  );
  return reasons.length ? `${label} — ${reasons.join(", ")}` : label;
}

// Sidebar pulse: a colored dot; at-risk entities get an animated ping ring.
export function HealthDot({ status }) {
  const meta = HEALTH_META[status] || HEALTH_META.healthy;
  const pulse = status === "at_risk";
  return (
    <span className="relative flex w-2.5 h-2.5 shrink-0" data-testid={`health-dot-${status}`}>
      {pulse && (
        <span
          className="animate-ping absolute inline-flex w-full h-full rounded-full opacity-60"
          style={{ backgroundColor: meta.color }}
        />
      )}
      <span className="relative inline-flex rounded-full w-2.5 h-2.5" style={{ backgroundColor: meta.color }} />
    </span>
  );
}

// Status pill (entity page header).
export function HealthBadge({ status }) {
  const { t } = useApp();
  const meta = HEALTH_META[status];
  if (!meta) return null;
  const pulse = status === "at_risk";
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium"
      style={{ backgroundColor: `${meta.color}22`, color: meta.color }}
      data-testid={`health-badge-${status}`}
    >
      <span className="relative flex w-1.5 h-1.5">
        {pulse && (
          <span className="animate-ping absolute inline-flex w-full h-full rounded-full opacity-70"
            style={{ backgroundColor: meta.color }} />
        )}
        <span className="relative inline-flex rounded-full w-1.5 h-1.5" style={{ backgroundColor: meta.color }} />
      </span>
      {t(meta.labelKey)}
    </span>
  );
}

// The "why" — chips for each contributing signal, colored by severity.
// When `entityKey` is given, each chip deep-links to the offending section.
export function HealthSignals({ signals, entityKey }) {
  const { t } = useApp();
  const navigate = useNavigate();
  if (!signals?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="health-signals">
      {signals.map((s) => {
        const color = SEVERITY_COLOR[s.severity] || SEVERITY_COLOR.low;
        const path = entityKey ? signalPath(s.key, entityKey) : null;
        const content = (
          <>
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
            {t(`health_sig_${s.key}`)}
            {s.count > 1 ? ` · ${s.count}` : ""}
          </>
        );
        const className = "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] border transition-colors";
        const style = { borderColor: `${color}55`, color };
        if (path) {
          return (
            <button
              key={s.key}
              type="button"
              onClick={(e) => { e.stopPropagation(); navigate(path); }}
              className={`${className} hover:bg-surface-hover cursor-pointer`}
              style={style}
              data-testid={`health-signal-${s.key}`}
            >
              {content}
            </button>
          );
        }
        return (
          <span key={s.key} className={className} style={style} data-testid={`health-signal-${s.key}`}>
            {content}
          </span>
        );
      })}
    </div>
  );
}
